import type { Course } from "@aybuben/core/content/course.ts";
import {
	PRACTICE_MODES,
	practiceSummary,
	type ModeSummary,
	type PracticeMode,
} from "@aybuben/core/practice.ts";
import { seasonPath, treeState, type TreeState } from "@aybuben/core/progress/tree.ts";
import type { GameState } from "@aybuben/core/state.ts";

import type { UiKey } from "../i18n/en.ts";

export const MODE_KEYS: Record<PracticeMode, { title: UiKey; body: UiKey }> = {
	mix: { title: "practice.mix", body: "practice.mixBody" },
	mistakes: { title: "practice.mistakes", body: "practice.mistakesBody" },
	letters: { title: "practice.letters", body: "practice.lettersBody" },
	words: { title: "practice.words", body: "practice.wordsBody" },
	listen: { title: "practice.listen", body: "practice.listenBody" },
	write: { title: "practice.write", body: "practice.writeBody" },
};

export interface ModeStatus {
	enabled: boolean;
	line: UiKey;
	n?: number;
}

/**
 * What a card says and whether it can be tapped. Mistakes counts its keys, not
 * what is due among them: a miss is worth fixing whether or not FSRS says so.
 * Disabled whenever its round would be empty — a lesson needs a first step.
 */
export function modeStatus(mode: PracticeMode, s: ModeSummary): ModeStatus {
	if (mode === "mistakes")
		return s.keys > 0
			? { enabled: true, line: "practice.fix", n: s.keys }
			: { enabled: false, line: "practice.noMistakes" };
	if (s.keys === 0) return { enabled: false, line: "practice.locked" };
	return s.due > 0
		? { enabled: true, line: "practice.due", n: s.due }
		: { enabled: true, line: "practice.fresh" };
}

export interface ModeCard {
	mode: PracticeMode;
	title: UiKey;
	body: UiKey;
	status: ModeStatus;
}

export interface PracticeModel {
	/** Nothing is trained yet: the tab is one note and no cards. */
	empty: boolean;
	subtitle: UiKey;
	/** Seasons of progress still to go before the tree is back in summer; the Water card shows above zero. */
	toSummer: number;
	tree: TreeState;
	cards: ModeCard[];
}

/** `now` is a parameter, not read here, so a view keeps render pure and decides when the counts refresh. */
export function practiceModel(st: GameState, course: Course, now: number): PracticeModel {
	const summary = practiceSummary(st, course, now);
	const empty = summary.mix.keys === 0;
	return {
		empty,
		subtitle: empty ? "practice.empty" : "practice.subtitle",
		toSummer: seasonPath(st, now).to.summer ?? 0,
		tree: treeState(st, now),
		cards: empty
			? []
			: PRACTICE_MODES.map((mode) => ({
					mode,
					title: MODE_KEYS[mode].title,
					body: MODE_KEYS[mode].body,
					status: modeStatus(mode, summary[mode]),
				})),
	};
}
